import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Container, Table, Spinner, Alert, Badge, Button, Modal } from "react-bootstrap";

const ServiceHistory = () => {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [modalVisible, setModalVisible] = useState(false);
    const [selectedTechnician, setSelectedTechnician] = useState(null);
    const [loadingTechnician, setLoadingTechnician] = useState(false);
    const navigate = useNavigate();

    const fetchServiceRequests = async () => {
        try {
            setLoading(true);
            setError(null);

            const token = localStorage.getItem("token");
            if (!token) {
                throw new Error("Please log in to view your service requests.");
            }

            const apiUrl = `${process.env.REACT_APP_API_URL || "https://madhusewingmachines.com"}/api/service-requests/my-requests`;

            const response = await axios.get(apiUrl, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });

            console.log("API Response:", response.data); // Debugging API response

            if (response.data.success) {
                setRequests(response.data.data);
            } else {
                throw new Error(response.data.message || "Failed to fetch service requests");
            }
        } catch (error) {
            let errorMessage = "Failed to load service requests";

            if (error.response) {
                if (error.response.status === 401) {
                    errorMessage = "Session expired. Please log in again.";
                    localStorage.removeItem("token");
                    navigate("/login");
                } else {
                    errorMessage = error.response.data?.message || errorMessage;
                }
            } else {
                errorMessage = error.message || errorMessage;
            }

            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchServiceRequests();
    }, []);

    const handleViewTechnician = async (item) => {
        console.log('ServiceHistory: View technician pressed for item:', item._id);
        setModalVisible(true);
        setLoadingTechnician(true);

        try {
            // Mock technician data - replace with actual API call
            const mockTechnicianData = {
                name: 'Kiran',
                phone: '9902742423',
                address: 'Sri Pavana Kunj, Singapura Village, Varadharaja Nagar, Bengaluru, Karnataka 560097, India',
                lastUpdated: '21 hours ago',
                coordinates: {
                    lat: 13.0768302,
                    lng: 77.538526
                }
            };

            // Simulate API delay
            setTimeout(() => {
                setSelectedTechnician(mockTechnicianData);
                setLoadingTechnician(false);
            }, 1000);

        } catch (error) {
            console.error('Error loading technician details:', error);
            setSelectedTechnician({
                name: 'Error Loading',
                message: 'Failed to load technician details. Please try again later.'
            });
            setLoadingTechnician(false);
        }
    };

    const handleViewOnGoogleMaps = () => {
        if (selectedTechnician?.coordinates) {
            const { lat, lng } = selectedTechnician.coordinates;
            const url = `https://www.google.com/maps?q=${lat},${lng}`;
            window.open(url, '_blank');
        }
    };

    const handleGetDirections = () => {
        if (selectedTechnician?.coordinates) {
            const { lat, lng } = selectedTechnician.coordinates;
            const url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
            window.open(url, '_blank');
        }
    };

    const handleCopyDetails = () => {
        if (selectedTechnician) {
            const details = `Technician: ${selectedTechnician.name}\nPhone: ${selectedTechnician.phone}\nAddress: ${selectedTechnician.address}\nCoordinates: ${selectedTechnician.coordinates?.lat}, ${selectedTechnician.coordinates?.lng}`;
            navigator.clipboard.writeText(details).then(() => {
                alert('Technician details copied to clipboard');
            }).catch(() => {
                alert('Failed to copy details');
            });
        }
    };

    // Fixed: Ensure `status` is checked before calling `toLowerCase()`
    const getStatusBadge = (status) => {
        if (!status || typeof status !== "string") return "secondary"; // Handle undefined/null status safely
        switch (status.toLowerCase()) {
            case "pending":
                return "warning";
            case "completed":
                return "success";
            case "cancelled":
                return "danger";
            case "in progress":
                return "primary";
            default:
                return "secondary";
        }
    };

    return (
        <Container className="p-1" style={{ marginTop: "100px" }}>
            <h2 className="mb-4">Your Service Requests</h2>

            {loading && (
                <div className="text-center">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                </div>
            )}

            {error && (
                <Alert variant="danger">
                    {error}
                    {error.includes("login") && (
                        <Button variant="primary" className="ms-3" onClick={() => navigate("/login")}>
                            Go to Login
                        </Button>
                    )}
                </Alert>
            )}

            {!loading && !error && requests.length === 0 && (
                <div className="text-center mt-5">
                    <h5>No service requests found.</h5>
                    <Button variant="success" className="mt-3" onClick={() => navigate("/services")}>
                        Request a Service
                    </Button>
                </div>
            )}

            {!loading && !error && requests.length > 0 && (
                <Table striped bordered hover responsive className="mt-4">
                    <thead>
                        <tr>
                            <th>Request ID</th>
                            <th>Request Date</th>
                            <th>Status</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {requests.map((request) => (
                            <tr key={request._id}>
                                <td>{request._id?.substring(18, 24).toUpperCase() || "N/A"}</td>
                                <td>{request.createdAt ? new Date(request.createdAt).toLocaleString() : "N/A"}</td>
                                <td>
                                    <Badge bg={getStatusBadge(request.status)}>
                                        {request.status || "Unknown"}
                                    </Badge>
                                    {request.completionOTP && (
                                        <div className="mt-2">
                                            <Badge bg="danger" className="fs-6 p-2">
                                                OTP: {request.completionOTP}
                                            </Badge>
                                            <div className="text-muted small mt-1">
                                                Expires: {request.otpExpiresAt ? new Date(request.otpExpiresAt).toLocaleTimeString() : 'N/A'}
                                            </div>
                                        </div>
                                    )}
                                </td>
                                <td>
                                    <Button
                                        variant="danger"
                                        size="sm"
                                        onClick={() => handleViewTechnician(request)}
                                        disabled={!request._id}
                                    >
                                        👁️ VIEW DETAILS
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}

            {/* Scrollable Technician Details Modal */}
            <Modal 
                show={modalVisible} 
                onHide={() => setModalVisible(false)}
                size="lg"
                centered
                scrollable
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        Technician Location - {selectedTechnician?.name || 'Loading...'}
                    </Modal.Title>
                </Modal.Header>
                
                <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto', paddingBottom: '30px' }}>
                    {loadingTechnician ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3">Loading technician details...</p>
                        </div>
                    ) : selectedTechnician ? (
                        <div>
                            {selectedTechnician.message ? (
                                <Alert variant="info">
                                    <i className="bi bi-info-circle me-2"></i>
                                    {selectedTechnician.message}
                                </Alert>
                            ) : (
                                <div style={{ paddingBottom: '20px' }}>
                                    <div className="mb-4">
                                        <h6 className="fw-bold text-dark">Technician:</h6>
                                        <p className="text-muted mb-0">{selectedTechnician.name}</p>
                                    </div>

                                    <div className="mb-4">
                                        <h6 className="fw-bold text-dark">Phone:</h6>
                                        <p className="text-muted mb-0">{selectedTechnician.phone}</p>
                                    </div>

                                    <div className="mb-4">
                                        <h6 className="fw-bold text-dark">Address:</h6>
                                        <p className="text-muted mb-0" style={{ lineHeight: '1.6' }}>
                                            {selectedTechnician.address}
                                        </p>
                                    </div>

                                    <div className="mb-4">
                                        <h6 className="fw-bold text-dark">Last Updated:</h6>
                                        <Badge bg="info" className="px-3 py-2">
                                            {selectedTechnician.lastUpdated}
                                        </Badge>
                                    </div>

                                    <div className="mb-4">
                                        <h6 className="fw-bold text-dark">Coordinates:</h6>
                                        <p className="text-danger fw-medium mb-0">
                                            Lat: {selectedTechnician.coordinates?.lat}, Lng: {selectedTechnician.coordinates?.lng}
                                        </p>
                                    </div>

                                    <div className="border-top pt-4 mt-4">
                                        <h6 className="fw-bold text-dark mb-3">Map Actions:</h6>
                                        
                                        <div className="d-grid gap-3">
                                            <Button
                                                variant="primary"
                                                onClick={handleViewOnGoogleMaps}
                                                className="d-flex align-items-center justify-content-center"
                                            >
                                                <i className="bi bi-geo-alt me-2"></i>
                                                📍 View on Google Maps
                                            </Button>

                                            <Button
                                                variant="outline-warning"
                                                onClick={handleGetDirections}
                                                className="d-flex align-items-center justify-content-center"
                                            >
                                                <i className="bi bi-compass me-2"></i>
                                                🧭 Get Directions
                                            </Button>

                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                onClick={handleCopyDetails}
                                                className="d-flex align-items-center justify-content-center align-self-end"
                                                style={{ width: 'fit-content', marginLeft: 'auto' }}
                                            >
                                                <i className="bi bi-clipboard me-2"></i>
                                                📋 Copy Details
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <Alert variant="danger">
                            Failed to load technician details
                        </Alert>
                    )}
                </Modal.Body>
                
                <Modal.Footer style={{ marginTop: '20px', marginBottom: '10px' }}>
                    <Button variant="secondary" onClick={() => setModalVisible(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default ServiceHistory;
